import type { HelpTopic } from '../core/index.js'

const STAFF = ['operator', 'facility_administrator'] as const

/** The facilities module's help topics (docs/plans/in-app-help-plan.md): the Scheduling tab of Configuration, one per group. */
export const FACILITIES_HELP_TOPICS: readonly HelpTopic[] = [
  {
    id: 'facilities.automation',
    title: 'Automation',
    seats: STAFF,
    summary: 'What the engine does on its own and where it stops for a person: open shifts at release, who releases the month, the census response and how open shifts are offered.',
    sections: [
      {
        heading: 'Open shifts at release',
        points: [
          'Most facilities cannot staff every seat from their own roster until they hire. This is the share of the month’s required seats the roster must fill before the month goes to workers - at least 20% by default; the rest is filled after the release from PRN, agency and the open board.',
          'Above it the release waits for a person. After the release the open seats go to PRN, agency and the open board.',
        ],
      },
      {
        heading: 'Releasing the month',
        points: [
          'By default the engine builds the month when bidding closes and releases it after a review window of four days, unless a block stands or someone holds it from the Dashboard’s Month tab.',
          'Staff only keeps the release a person’s press. Whoever the last row names may release past a block with a reason code.',
        ],
      },
      {
        heading: 'Open shift lifecycle',
        points: [
          'An open shift is a seat nobody holds for an unplanned reason - a call-out, a census rise, a Bridge callout nobody answered, a worker who left, a seat the roster could not cover - not an assigned shift from the planned month. The engine works it from the moment it is born until the shift starts, and it moves whether or not anyone is looking: the board wakes itself at each stage.',
          'First come, the default: every eligible worker may take the seat from the day it is posted, the first to accept gets it, and the race runs to the shift’s start. Nobody assigns a worker by hand, before the release or after it, so no bias can be alleged. The facility’s order offers one worker at a time in the order below instead, then everyone first come inside the urgent line, and your override assigns in the last hours.',
          'The stages, counted back from the shift’s start: posted (7 days by default; first come, each worker gets one daily digest of the seats newly open to them, a seat still open repeated after the nudge interval); urgent (24 hours: everyone eligible told at once, through quiet hours, and again every so many hours to those who have not answered); the engine’s Clinical Bridge callout (12 hours, where Bridge is on); the lock (4 hours: the seat comes to Needs you); started (a seat still open is closed as ended unfilled and logged, and Needs you reminds you to record it ran short or leave it).',
          'Only eligible workers are ever told, at every stage: the right role or substitution, unit, credentials in date, free that day, under the hours rules, still on the roster. A late seat is urgent, not less particular.',
          'The reliability score still orders the planned month, the trade board’s candidates and the Clinical Bridge boost. First come it no longer orders who is offered an open seat. A seat taken inside the urgent line earns the late-fill points once worked, in either mode.',
        ],
      },
    ],
    related: ['schedule.today', 'facilities.scheduling-census-response'],
  },
  {
    id: 'facilities.scheduling-shift-types',
    title: 'Shift types and the usual day',
    seats: STAFF,
    summary: 'Which shift types this facility runs, their standard hours, and which type a day gets when the engine places a worker itself.',
    sections: [
      {
        heading: 'The shift types',
        points: [
          'Switch a type off and workers are no longer asked about it in their preferences, and the engine never places anyone on it, even where a shift of that type is published.',
          'The hours are the standard times. Workers see these hours while choosing preferences, and a change shows at once. Shifts already published keep the times they were published with; where those differ, the worker is told the published times too.',
          'A night shift that ends the next morning is fine: 11:00 PM to 7:00 AM is one shift.',
          'Minutes unpaid is the meal break inside the shift. It comes off the hours that count toward HPPD, so an 8-hour shift with a 30-minute unpaid lunch counts 7.5 hours and the staffing plan asks for more seats to reach its target.',
        ],
      },
      {
        heading: 'Saturday, Sunday and weekday are usually',
        points: [
          'This is the shift type the engine picks on that day when it is choosing for the worker rather than following their preferences.',
          'That happens in two cases only: a tier that does not bid (probationary by default) has its whole month assigned, and a Bronze worker has a Medium day off turned into a working day or their first PTO day denied.',
          'Workers who bid get their days from their own ranked preferences. This setting does not move them.',
          'The engine can only place a shift that is published. If no Saturday evening is published, a Saturday evening default places nothing.',
        ],
      },
      {
        heading: 'Good to know',
        points: [
          'It is not a way to fill a hard shift. It leans only the few placements above towards it, and those land on new staff and Bronze workers.',
          'To cover a shift that is hard to fill, publish enough seats for it and set a minimum staffing line on the Details tab. Seats left unfilled go to the open board after the release.',
        ],
      },
    ],
    related: ['facilities.scheduling-bidding', 'facilities.scheduling-staffing'],
  },
  {
    id: 'facilities.scheduling-bidding',
    title: 'Who bids, and the caps',
    seats: STAFF,
    summary: 'Which tiers choose their own month, and the hours, days off and PTO each tier and employment type may ask for.',
    sections: [
      {
        heading: 'Who bids',
        points: [
          'A tier that bids saves preferences and the engine follows them.',
          'A tier switched off is assigned its month from the rules, weekends first when that setting is on, and is never reminded to save preferences.',
          'Mark the days they can work: an employment type switched on here (part time and PRN by default) marks availability in the wizard instead of days off. They are scheduled on those days and no others, and a month short of their floor is flagged, never refused.',
        ],
      },
      {
        heading: 'The caps',
        points: [
          'The table has a cell for every tier and employment type. Tap a cell to edit it below the table.',
          'Hours a month, at most, is always a hard limit. When the floor equals the ceiling the month is fixed, and the worker sees their hours as a statement rather than a choice.',
          'High priority days off are avoided at all costs but never promised: only a seat a minimum line is short on, with nobody off that day at Low or Medium, takes one (see Drafting rules). Medium and Low days are not limited here.',
          'PTO days a month is counted beside the worker’s own balance: the smaller of the two applies. Blank means the balance alone.',
          'Medium days overridden to work and First PTO day denied are the final adjustments, Bronze’s by default. They are applied when the draft is built.',
        ],
      },
      {
        heading: 'The floor',
        points: [
          'Refused: a worker cannot save preferences that ask for fewer hours than the floor.',
          'Drafted and flagged: they can, and the draft carries a flag for the manager.',
          'With contracted hours on, a worker whose record has contracted hours a week gets those (over 52 weeks, spread across 12 months) as their floor instead of the cell’s.',
        ],
      },
    ],
    related: ['facilities.scheduling-drafting', 'schedule.build-the-month'],
  },
  {
    id: 'facilities.scheduling-drafting',
    title: 'How a month is drafted',
    seats: STAFF,
    summary: 'The rules the engine checks before it places each shift, for every worker’s draft and for Build the month.',
    sections: [
      {
        heading: 'Rules that keep a shift off',
        points: [
          'Shifts in a week, at most: counted Sunday to Saturday.',
          'Rest between shifts and Hours straight: a shift that would leave too little rest, or run into another past the limit, is not placed.',
          'Days in a row: the run of working days that is not allowed. 6 means five in a row is the most.',
          'No day shift after an evening: when on, nobody is drafted, swapped onto or offered the morning after an evening.',
          'A blank number switches its rule off.',
        ],
      },
      {
        heading: 'Rules that place the shift and say so',
        points: [
          'Weekly overtime: a week past this is placed and marked for the manager to review.',
          'Daily overtime: a shift longer than this is placed and flagged.',
        ],
      },
      {
        heading: 'How the month is shaped',
        points: [
          'Spread the month evenly shares the worker’s hours across the weeks before filling any one week.',
          'Low priority days off: With free days treats them like any other free day; After free days holds them back until the minimum hours need them, and flags it.',
          'Assigned months take weekends first fills the weekends of an assigned month before the weekdays.',
          'A seat still short of its minimum: after everyone is drafted, Take a day off fills it from the bidders off that day - Low days first, then Medium, then High, the lowest-ranked bidder first, their count held - and Leave it open sends it to the board. A seat no minimum line requires is left open either way.',
        ],
      },
      {
        heading: 'PTO, timing and ties',
        points: [
          'PTO is refused past the balance stops a worker asking for more PTO than they have.',
          'PTO days on a draft: Approved at once; Decided by the run, which can deny a day when it would leave a minimum staffing line short; or Decided by a person.',
          'Preferences are taken for the earliest month whose close is still ahead and, if you choose, the one after.',
          'When two workers want the same shift, the higher tier gets it, then the higher score, then the earlier hire date when ties break by seniority.',
        ],
      },
    ],
    related: ['facilities.scheduling-bidding', 'schedule.build-the-month'],
  },
  {
    id: 'facilities.scheduling-staffing',
    title: 'Units, credentials and minimum staffing',
    seats: STAFF,
    summary: 'Who may work where, what each role must hold, and how many people a shift needs. Leave them all empty and scheduling works as before.',
    sections: [
      {
        heading: 'Units',
        points: [
          'List the facility’s units here. A worker’s record names their home unit and the units they float to.',
          'The census and the staffing plan are reckoned per unit, so with no units listed no census can be recorded and the plan requires nothing. Where shifts are already published, List these units adds the units they name in one step; give each its beds and planned census after.',
          'Each unit has an id and a name. The id is the code published shifts, worker records and imports carry, so keep it once it is in use; the name is what people see and can change at any time.',
          'A worker with no unit on their record may work any unit.',
          'A swap that moves a shift off the taker’s home unit waits for staff to approve it.',
        ],
      },
      {
        heading: 'Credentials',
        points: [
          'The codes every worker of a role must hold, such as BLS. A published shift can ask for more.',
          'A worker missing one, or whose credential has expired by the shift’s date, is not placed on that shift, offered it or swapped onto it.',
        ],
      },
      {
        heading: 'Minimum staffing',
        points: [
          'Each line says how many people a shift type needs, for one unit or every unit, and for one role or every role.',
          'A line applies to every day of the month. It cannot yet be set for one weekday, such as Saturday evenings only.',
          'For a state ratio, fill in "per so many residents": 1 CNA per 10 residents asks for 10 CNAs at a census of 100 and 11 at 105. It is judged against the day’s census, or the forecast, and not on a day with neither.',
          'Build the month lists the dates that fall short of a line. When PTO is decided by the run, a PTO day is denied when it would leave a line short and the worker could fill it.',
          'A line does not publish shifts. Publish enough seats for it to be met.',
        ],
      },
      {
        heading: 'Who may cover whom',
        points: [
          'Tick the seats a role may fill beside its own, as your state allows: usually a nurse may cover a CNA seat, never the reverse, and an RN may cover an LPN.',
          'A covered seat counts toward its own role: an RN on a CNA seat is one of the CNAs the line and the plan ask for.',
          'It reaches the month’s build, the open board, swaps, the supervisor’s override and the census response. Nothing ticked keeps every seat to its own role.',
        ],
      },
    ],
    related: ['facilities.scheduling-shift-types', 'schedule.build-the-month'],
  },
  {
    id: 'facilities.scheduling-census',
    title: 'Census and the staffing plan',
    seats: STAFF,
    summary: 'How many residents each unit holds, and how a day’s census becomes the seats each shift needs.',
    sections: [
      {
        heading: 'The words',
        points: [
          'PPD is patient days: one resident in a bed for one day. A day’s census is its PPD; a month’s PPD is its days added up.',
          'HPPD is hours per patient day: productive nursing hours divided by PPD, total and by role. It is how staffing is planned, regulated and reported.',
        ],
      },
      {
        heading: 'Units',
        points: [
          'In order: list the units on the Details tab, set the plan here, then record the census each day on the Dashboard’s Today tab. Until the first two are done this group shows the steps with a button to the units.',
          'Give each unit its beds and a planned census. A forecast is capped at the beds; the planned census stands in for a day with no count and no history.',
          'An operator or a facility administrator may list the units and set the plan for their facility.',
        ],
      },
      {
        heading: 'The plan',
        points: [
          'HPPD: a target per role and the share of the day’s hours each shift type carries. A day’s hours for a role are PPD × target, and seats are those hours over the shift’s productive length - its span less any unpaid break set on the Shift types tab - rounded up.',
          'Licensed nurses: a target for licensed nurses together asks for LPN seats, which an RN fills where Details › Who may cover whom allows it.',
          'Rounding: up, to the nearest, or whole seats with a short shift. With short shifts, 10.5 seats is 10 and one short shift at the busiest hours, 10.4 is 10; set the length, the fraction and the start for each shift type. It keeps the day close to target where rounding up would overshoot.',
          'Grid: bands of residents with the seats each shift and role needs, the way many facility assessments are written.',
          'Fixed positions are added whatever the census. The minimum staffing lines above are floors the plan never goes below.',
          'Load the example to start from 3.5 HPPD (0.5 RN, 0.6 LPN, 2.4 CNA) split 40 / 35 / 25. It is an example, not a state’s rule: replace it with your facility assessment’s numbers.',
        ],
      },
      {
        heading: 'Workforce',
        points: [
          'The hours a full-time equivalent works a week and the buffer for PTO, call-offs and turnover. The Workforce report on the Reporting tab uses them to turn the plan into people to employ; they change no schedule.',
        ],
      },
    ],
    related: ['schedule.census', 'facilities.scheduling-staffing'],
  },
  {
    id: 'facilities.scheduling-census-response',
    title: 'Census response',
    seats: STAFF,
    summary: 'What the released month does when the census moves: who gives up a seat when it falls, what posts when it rises, and what never happens by itself.',
    sections: [
      {
        heading: 'The rule',
        points: [
          'When the census falls, shifts are released from the lowest reliability tier first, so a higher tier keeps what it bid for. Before that, volunteers, agency and PRN staff; inside a step, the lowest score then the least senior.',
          'Before anyone is flexed they are floated: moved to an open seat on another unit for the same date and shift type, when they qualify for it.',
          'A minimum staffing line is never undercut. Where the line is what the seats are, nothing is released and the evaluation says the minimum holds.',
          'When the census rises, the extra seats go to the open board, offered down the add order - PRN then agency by default; part-time staff and full-time staff as overtime can be added and ordered - then to everyone of the role first come.',
          'The fall’s order can also release staff past the weekly overtime line first, and part-time staff, where your policy says so.',
        ],
      },
      {
        heading: 'Automatic or proposed',
        points: [
          'Automatic: the engine acts as soon as a census is recorded. Propose: it prepares the change on the Census tool and a person applies it.',
          'When the shift starts within the notice hours or the lock hours, nothing is automatic either way: the change is proposed for the facility administrator.',
          'A flexed worker is told the policy in words, never another person’s name or tier, and has first right to the seat back if the census returns within the recall hours.',
          'The tolerance keeps the engine from moving on every admission: a difference inside it is no change.',
        ],
      },
      {
        heading: 'Where it stands',
        points: ['The Census tool’s What-if shows what a count would change before it is recorded, and What the engine did shows what each recorded count did to the month.'],
      },
    ],
    related: ['schedule.census', 'facilities.scheduling-census', 'facilities.scheduling-timeline'],
  },
  {
    id: 'facilities.scheduling-timeline',
    title: 'The timeline and the coverage ladder',
    seats: STAFF,
    summary: 'When a month’s preferences close and its schedule is released, and how a gap is covered after the release.',
    sections: [
      {
        heading: 'Before the month',
        points: [
          'Bid window on: bidding for a month opens this many days before it, at this time in the facility’s zone, and runs this many hours. Everyone bids in the same window, and workers who bid get a message when it opens. Switched on, it starts at 31 days, 6:00 AM and 72 hours.',
          'Bid window off: workers can save preferences from when a month’s shifts are published until the close, at midnight this many days before the month starts.',
          'Either way, after the close workers cannot save preferences for that month.',
          'Build the month at the close: when preferences close, the engine builds the month from everyone’s preferences, ranked by tier, then score, then seniority. Nothing reaches workers until staff release it. Off, staff press Build themselves.',
          'Draft locks and Schedule released set the dates on the month’s timeline in Build the month. Staff still press Release to send the schedule.',
          'Change window after release: for this many hours workers can ask to change a shift type on the same day or swap with a named peer. Requests are settled when it closes, by score and then seniority, not first come.',
        ],
      },
      {
        heading: 'The coverage ladder',
        points: [
          'Trade board: workers offer a shift to a peer, who has the answer time to accept before it moves to the next peer. It closes this many days before the shift.',
          'Open board: unfilled and vacated shifts are offered to PRN and agency staff first, one at a time for the offer time, from when it opens.',
          'Open board closes: inside this many hours of the shift, everyone of the role not working that day is told at once, and the first to accept gets it.',
          'The lock hours: in this many hours before a shift the engine stops offering an open seat and nothing changes except a supervisor’s assignment with a reason code. Screens say such a shift "starts within N hours" or is "too close to offer". A Clinical Bridge callout opened this close is marked Close to the start.',
        ],
      },
    ],
    related: ['schedule.build-the-month', 'facilities.scheduling-bridge'],
  },
  {
    id: 'facilities.scheduling-bridge',
    title: 'Clinical Bridge pre-bidding',
    seats: STAFF,
    summary: 'Who may sign up for Clinical Bridge days, how many days a month, and when pulling out counts as late.',
    sections: [
      {
        heading: 'The settings',
        points: [
          'Who may enroll: Staff’s say means each worker is marked eligible on their record. By employment type means part time and PRN only while overtime is off at the facility, and everyone when it is on.',
          'A worker cannot enroll for a day their submitted schedule already has a shift on.',
          'May enroll for, a month: the most Bridge days each employment type can sign up for. No cap leaves it open.',
          'Late withdrawal: pulling out of a day within this many hours of the day’s first shift is allowed but recorded, and takes a point off the worker’s score.',
          'Escalate an unanswered callout after: a callout nobody has taken by then goes to the open board.',
        ],
      },
      {
        heading: 'Good to know',
        points: [
          'Switching Clinical Bridge on, and its response window, boost and premium, are on the Clinical Bridge tab.',
          'Accepting a callout changes no score. Bridge points come only when the shift is worked.',
        ],
      },
    ],
    related: ['facilities.scheduling-timeline'],
  },
  {
    id: 'facilities.bid-windows',
    title: 'Bid window timing',
    seats: STAFF,
    summary: 'What workers are told about when each tier’s bidding opens. It is wording only: bidding runs in the one preferences window on the Scheduling tab.',
    sections: [
      {
        heading: 'Gold, Silver and Bronze window opens',
        points: [
          'Free text, written as a worker should read it, such as “1st of month · 6:00 AM”.',
          'A worker sees the line for the tier above their own: on their standing, beside the points they need, and in the message that tells them the next tier is within reach (“Silver’s bid window opens …”).',
          'Not set means the worker is told nothing about that tier’s window.',
        ],
      },
      {
        heading: 'The other lines',
        points: [
          'Scoring period closes describes when a month’s scores become final. Scoring periods are calendar months in the facility’s time zone, and this text does not move the close.',
          'Gold window duration is kept with the configuration but nothing reads it yet.',
          'Probationary shows how the lowest tier gets its month. Which tiers bid and which are assigned is set under Who bids on the Scheduling tab.',
        ],
      },
      {
        heading: 'Good to know',
        points: [
          'When bidding opens and closes is the Scheduling tab’s timeline, where the bid window is set: every tier that bids saves preferences in the same window, and the month is built from them, contested shifts going by tier, then score, then seniority.',
          'Keep this wording in step with that timeline, so what workers read matches when they can actually bid.',
          'Saving applies every settings tab’s changes at once.',
        ],
      },
    ],
    related: ['facilities.scheduling-bidding', 'facilities.scheduling-timeline'],
  },
  {
    id: 'facilities.config-details',
    title: 'Facility details',
    seats: STAFF,
    summary: 'The facility’s name, time zone, address and phone.',
    sections: [
      {
        heading: 'What each one does',
        points: [
          'The time zone is the one every scoring period, bid window and schedule date is reckoned in. Until it is set the facility reads as UTC.',
          'Changing the time zone at a facility that already has shift outcomes moves the running scores at the next recalculation, because the period boundaries move.',
          'A US ZIP code suggests a time zone for you to confirm. It only fills the zone while none is set.',
          'The address and phone show in the dashboard header, the facility list and the admin’s view of the facility.',
        ],
      },
      {
        heading: 'Who changes them',
        points: ['An operator, or a facility administrator for their own facilities, edits every field here.'],
      },
      {
        heading: 'The facility’s administrators',
        points: [
          'Under the details the operator sees the facility’s administrators: invite one, or choose a name to edit their details, send a reset link or remove them.',
          'Removing an administrator from their only facility removes their login entirely; from one of several, it takes away just this facility.',
          'Every removal is kept in SeeWhoCares’s configuration history with who did it, when, and the login as it was.',
        ],
      },
    ],
    related: ['facilities.config-scoring'],
  },
  {
    id: 'facilities.config-behaviors',
    title: 'Behaviors',
    seats: [...STAFF, 'admin'],
    summary: 'What is measured for every worker, how much each behavior counts, and what earns full marks and none.',
    sections: [
      {
        heading: 'The master and each facility',
        points: [
          'The master configuration applies to every facility. A facility follows the master for any behavior it has not set itself, so a later change to the master reaches it.',
          'Setting a value at a facility overrides the master there only. Everyone at one facility is measured the same way.',
          'A facility administrator can set values for their own facilities; the master is the operator’s.',
        ],
      },
      {
        heading: 'Each behavior',
        points: [
          'A behavior that is off, or has no weight, does not count towards the score.',
          'The score is the weighted average of the behaviors that are on, weighted and have data in the period.',
          'Each behavior’s measure becomes a value from 0 to 100 between the level that earns 100 and the level that earns 0.',
          'Custom behaviors are reported by your scheduling partner as events. A per-period minimum is met or not: 100 from the minimum, 0 below it.',
        ],
      },
      {
        heading: 'Good to know',
        points: ['Two people saving at once cannot overwrite each other: the second save is refused rather than overwriting the first.'],
      },
    ],
    related: ['facilities.config-scoring', 'facilities.config-tiers'],
  },
  {
    id: 'facilities.config-scoring',
    title: 'Scoring rules',
    seats: STAFF,
    summary: 'How this facility’s shift outcomes become a score: when a score is final, what counts as late or short notice, and the points added or taken off on top.',
    sections: [
      {
        heading: 'The score itself',
        points: [
          'Shifts before a score is final: with fewer shifts in a period the score is provisional, blended with the previous final score in proportion to the shifts worked.',
          'Grace for arrival and departure: a shift is on time when the worker arrives and leaves within this many minutes of the scheduled times.',
          'Short notice: a call-out less than this many hours before the shift counts as this many call-outs.',
        ],
      },
      {
        heading: 'Tiers at the close',
        points: [
          'New hires stay probationary for this many days from their hire date, whatever their score.',
          'A provisional score can lower a tier: when off, a worker with too few shifts keeps their tier rather than dropping on thin evidence. A provisional score can always raise one.',
        ],
      },
      {
        heading: 'Points on top',
        points: [
          'A denied PTO day the worker then works, and a shift taken first come inside 24 hours and worked, each add points. A supervisor’s assignment never earns the late-fill points.',
          'Withdrawing from a Clinical Bridge day inside the cutoff takes points off.',
          'Points are added only once the shift is reported worked. 0 switches one off.',
        ],
      },
    ],
    related: ['facilities.config-behaviors', 'facilities.config-tiers'],
  },
  {
    id: 'facilities.config-tiers',
    title: 'Tier thresholds',
    seats: STAFF,
    summary: 'The score a worker needs for Gold, Silver and Bronze. Below Bronze is Probationary.',
    sections: [
      {
        heading: 'When a tier changes',
        points: [
          'A tier changes only when a period closes, from the final score against these thresholds.',
          'In the middle of a period a worker’s score and tier can disagree. Their standing shows the tier they are on track for at the close beside the one they hold.',
          'New hires stay probationary for the days set on Scoring Rules, and a provisional score may be kept from lowering a tier there too.',
        ],
      },
      {
        heading: 'What the tier decides',
        points: [
          'Who wins a shift two workers want when the month is built: tier first, then score, then the tie rule on the Scheduling tab.',
          'Each tier’s caps on hours, PTO and High priority days off, set on the Scheduling tab, and which tiers bid at all.',
          'The order a scheduling partner’s pool comes back in, where Partner Prioritization is on.',
        ],
      },
      {
        heading: 'Good to know',
        points: [
          'Raising a threshold moves nobody until the next close.',
          'Tiebreaker within tier is a description only. The month’s build breaks ties by the Scheduling tab’s rule, and a partner’s ranking keeps the partner’s own order for workers level on tier and score.',
        ],
      },
    ],
    related: ['facilities.config-scoring', 'facilities.scheduling-bidding'],
  },
  {
    id: 'facilities.config-bridge',
    title: 'Clinical Bridge',
    seats: STAFF,
    summary: 'On-demand coverage: eligible workers sign up for days they would cover a call-out, and a facility administrator opens a callout to them.',
    sections: [
      {
        heading: 'The switches',
        points: [
          'Clinical Bridge enabled: off stops new sign-ups. Days already signed up for stay on record.',
          'OT permitted: when off, a worker whose known hours that week plus the shift would pass 40 is held back from a callout’s pool, and only part-time and PRN workers may sign up where eligibility follows employment type.',
        ],
      },
      {
        heading: 'A callout',
        points: [
          'Response window: how long the pool has to answer once a callout opens. 0 means until the shift starts. The first to accept gets it.',
          'Arrival window: how long after accepting the worker has to arrive.',
          'Score boost per Bridge shift worked: added only once the shift is reported worked. Accepting a callout changes no score.',
        ],
      },
      {
        heading: 'Good to know',
        points: [
          'The pay premium is shown to workers with the Bridge terms. autonoME records it; pay is your payroll’s.',
          'Unfilled escalation path and Score recalc frequency are kept with the configuration but nothing reads them yet. An unanswered callout escalates to the open board after the minutes set on the Scheduling tab.',
          'Who is eligible is set per worker on the Edit worker form or by the roster import.',
        ],
      },
    ],
    related: ['facilities.scheduling-bridge', 'facilities.config-scoring'],
  },
  {
    id: 'facilities.config-prioritization',
    title: 'Partner prioritization',
    seats: STAFF,
    summary: 'Whether a scheduling partner that asks this facility to rank its workers gets them back in reliability order.',
    sections: [
      {
        heading: 'What it does',
        points: [
          'On: the pool comes back ordered by tier, then score. Workers the facility has no record of come last.',
          'Off: the partner gets its own order back unchanged.',
          'Either way no worker is added to or removed from the pool, and every ranking asked for is kept on record.',
        ],
      },
    ],
    related: ['facilities.config-tiers'],
  },
  {
    id: 'facilities.config-add-ons',
    title: 'Add-ons',
    seats: STAFF,
    summary: 'Features beyond the core that your agreement with SeeWhoCares includes, switched on per facility.',
    sections: [
      {
        heading: 'How they work',
        points: [
          'An add-on works at a facility when your license for it is in force and the facility is switched on.',
          'An operator, or a facility administrator for their own facilities, switches each one on or off here.',
          'Switching one off makes it read-only at that facility. Nothing is deleted, and switching it back on picks up where it left off.',
          'A license may cover a limited number of facilities. Switching on beyond it is refused.',
        ],
      },
    ],
    related: [],
  },
]
